import { initializeApp, FirebaseApp } from "firebase/app";
import { getAuth, Auth } from "firebase/auth";
import { getFirestore, Firestore } from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";

// Check if all required environment variables are present
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN', 
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
];

const missingEnvVars = requiredEnvVars.filter(envVar => !import.meta.env[envVar]);

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;
let dbInstance: Firestore | null = null;
let storageInstance: FirebaseStorage | null = null;

if (missingEnvVars.length === 0) {
  const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  try {
    // Initialize Firebase
    app = initializeApp(firebaseConfig);
    authInstance = getAuth(app);
    dbInstance = getFirestore(app);
    storageInstance = getStorage(app);
  } catch (error) {
    console.error('Firebase initialization failed:', error);
  }
} else {
  console.warn('Firebase not initialized. Missing environment variables:', missingEnvVars);
  console.warn('To enable Firebase features, please set the following environment variables:');
  missingEnvVars.forEach(envVar => console.warn(`- ${envVar}`));
}

// Create mock functions for when Firebase is not configured
const createMockAuth = (): any => ({
  currentUser: null,
  onAuthStateChanged: () => () => {},
  signInWithEmailAndPassword: () => Promise.reject(new Error('Firebase not configured')),
  signOut: () => Promise.reject(new Error('Firebase not configured')),
});

const createMockDb = (): any => ({
  collection: () => Promise.reject(new Error('Firebase not configured')),
  doc: () => Promise.reject(new Error('Firebase not configured')),
  addDoc: () => Promise.reject(new Error('Firebase not configured')),
  getDocs: () => Promise.reject(new Error('Firebase not configured')),
  getDoc: () => Promise.reject(new Error('Firebase not configured')),
  updateDoc: () => Promise.reject(new Error('Firebase not configured')),
  deleteDoc: () => Promise.reject(new Error('Firebase not configured')),
});

const createMockStorage = (): any => ({
  ref: () => Promise.reject(new Error('Firebase not configured')),
  uploadBytes: () => Promise.reject(new Error('Firebase not configured')),
  getDownloadURL: () => Promise.reject(new Error('Firebase not configured')),
});

export const auth = authInstance || createMockAuth();
export const db = dbInstance || createMockDb();
export const storage = storageInstance || createMockStorage();
export default app;
